import React, { forwardRef } from 'react'

import { errorClasses, labelClasses } from '@/app/components/Form/styles'
import { cn } from '@/app/utils/style'

const RADIO_VARIANTS = {
  light: {
    input: {
      base: 'appearance-none size-4 rounded-full border cursor-pointer transition-all',
      unchecked: {
        default: 'bg-neutral-50 border-neutral-50',
        hover: 'hover:bg-neon-100 hover:border-neon-100',
        active: 'active:bg-neon-200 active:border-neon-200',
        disabled: 'bg-neutral-300 border-neutral-300 cursor-not-allowed',
      },
      checked: {
        default:
          'relative bg-neon-200 border-neon-200 before:content-[""] before:absolute before:inset-0 before:m-auto before:w-2 before:h-2 before:rounded-full before:bg-white-950',
        hover: 'hover:bg-neon-100 hover:border-neon-100 hover:before:bg-white-800',
        active: 'active:bg-neon-100 active:border-white-700 active:before:bg-white-800',
        disabled: 'bg-neutral-300 border-neutral-300 before:bg-white-950',
      },
    },
    label: {
      default: 'text-white-950',
      disabled: 'text-white-800',
    },
  },
  dark: {
    input: {
      base: 'appearance-none size-4 rounded-full border cursor-pointer transition-all',
      unchecked: {
        default: 'bg-white border-white',
        hover: 'hover:bg-neon-100 hover:border-neon-100',
        active: 'active:bg-neon-200 active:border-white',
        disabled: 'bg-neutral-300 border-neutral-300 cursor-not-allowed',
      },
      checked: {
        default:
          'relative bg-neon-200 border-neon-200 before:content-[""] before:absolute before:inset-0 before:m-auto before:w-2 before:h-2 before:rounded-full before:bg-white-950',
        hover: 'hover:bg-neon-100 hover:border-neon-100 hover:before:bg-white-800',
        active: 'active:bg-neon-100 active:border-white active:before:bg-white-800',
        disabled: 'bg-neutral-300 border-neutral-300 before:bg-white',
      },
    },
    label: {
      default: 'text-white',
      disabled: 'text-white-100',
    },
  },
} as const

export interface RadioOption {
  label: string
  value: string
  disabled?: boolean
}

export interface RadioGroupProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  helperText?: string
  options: RadioOption[]
  variant?: 'light' | 'dark'
  classnames?: {
    container?: string
    label?: string
    wrapper?: string
    option?: string
    input?: string
    optionLabel?: string
  }
}

export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      className,
      name,
      required,
      variant = 'light',
      classnames = {},
      value: selectedValue,
      ...props
    },
    ref,
  ) => {
    const groupId = name || `radio-group-${label?.toLowerCase().replace(/\s+/g, '-')}`
    const variantStyles = RADIO_VARIANTS[variant]

    return (
      <div className={cn('flex flex-col', classnames.container)}>
        {label && (
          <label className={cn(labelClasses, classnames.label)}>
            {label}
            {required && <span className="text-semantic-error ml-xs">*</span>}
          </label>
        )}
        <div
          className={cn('flex flex-row gap-4xl', classnames.wrapper)}
          role="radiogroup"
          aria-labelledby={label ? `${groupId}-label` : undefined}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${groupId}-error` : helperText ? `${groupId}-helper` : undefined
          }
        >
          {options.map((option, index) => {
            const optionId = `${groupId}-${option.value}`
            const isFirstOption = index === 0
            const isChecked = selectedValue === option.value

            return (
              <div key={option.value} className={cn('flex items-center gap-sm', classnames.option)}>
                <input
                  ref={isFirstOption ? ref : undefined}
                  type="radio"
                  id={optionId}
                  name={name}
                  value={option.value}
                  checked={isChecked}
                  disabled={option.disabled}
                  className={cn(
                    variantStyles.input.base,
                    option.disabled
                      ? isChecked
                        ? variantStyles.input.checked.disabled
                        : variantStyles.input.unchecked.disabled
                      : [
                          isChecked
                            ? [
                                variantStyles.input.checked.default,
                                variantStyles.input.checked.hover,
                                variantStyles.input.checked.active,
                              ]
                            : [
                                variantStyles.input.unchecked.default,
                                variantStyles.input.unchecked.hover,
                                variantStyles.input.unchecked.active,
                              ],
                        ],
                    classnames.input,
                  )}
                  required={required}
                  {...props}
                />
                <label
                  htmlFor={optionId}
                  className={cn(
                    'size-body-sm font-normal cursor-pointer',
                    option.disabled ? variantStyles.label.disabled : variantStyles.label.default,
                    option.disabled && 'cursor-not-allowed',
                    classnames.optionLabel,
                  )}
                >
                  {option.label}
                </label>
              </div>
            )
          })}
        </div>
        {error && (
          <span id={`${groupId}-error`} className={errorClasses}>
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${groupId}-helper`} className="size-body-sm font-normal text-white-700 mt-xs">
            {helperText}
          </span>
        )}
      </div>
    )
  },
)

RadioGroup.displayName = 'RadioGroup'
